import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Wifi, RotateCcw, AlertTriangle } from 'lucide-react';
import { useNetworkStatus } from '../../hooks/usePWA';
import { pwa } from '../../utils/pwa';

interface OfflineIndicatorProps {
  className?: string;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  className = ''
}) => {
  const { isOnline, wasOffline } = useNetworkStatus();
  const [showReconnected, setShowReconnected] = useState(false);
  const [pendingSync, setPendingSync] = useState(false);

  useEffect(() => {
    if (isOnline && wasOffline) {
      setShowReconnected(true);
      setPendingSync(true);
      
      // Trigger background sync
      pwa.scheduleBackgroundSync().then(() => {
        setPendingSync(false);
      });

      // Hide reconnected message after 3 seconds
      const timer = setTimeout(() => {
        setShowReconnected(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  if (isOnline && !showReconnected) {
    return null;
  }

  if (showReconnected) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed top-4 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-auto z-50 ${className}`}
        >
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-4 py-3 shadow-lg backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {pendingSync ? (
                  <RotateCcw className="w-5 h-5 text-emerald-600 dark:text-emerald-400 animate-spin" />
                ) : (
                  <Wifi className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                )}
              </div>
              
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-900 dark:text-emerald-100">
                  {pendingSync ? 'Syncing data...' : 'Back online!'}
                </p>
                <p className="text-xs text-emerald-700 dark:text-emerald-300">
                  {pendingSync 
                    ? 'Updating your nutrition data' 
                    : 'All your offline changes have been saved'
                  }
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className={`fixed top-4 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-auto z-50 ${className}`}
      >
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <WifiOff className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                You're offline
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Changes will be saved and synced when you're back online
              </p>
            </div>
            
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Minimal offline indicator for status bars
export const OfflineStatus: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isOnline } = useNetworkStatus();

  if (isOnline) {
    return null;
  }

  return (
    <div className={`flex items-center gap-1 text-amber-600 dark:text-amber-400 ${className}`}>
      <WifiOff className="w-4 h-4" />
      <span className="text-xs font-medium">Offline</span>
    </div>
  );
};

// Offline banner for full-width notifications
export const OfflineBanner: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isOnline } = useNetworkStatus();

  if (isOnline) {
    return null;
  }

  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      exit={{ height: 0 }}
      className={`bg-amber-100 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 ${className}`}
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-center gap-2 text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">
            You're currently offline. Some features may be limited.
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default OfflineIndicator;